import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Button,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useMessageStore } from '@/stores/useMessageStore.js';
import { useAppMode } from '@/hooks/useAppMode.js';
import { useVirtualMessageBus } from '@/hooks/useVirtualMessageBus.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { getMessageDefinitions } from '@k-ucap/message-dictionary';
import { ValidationSeverity } from '@k-ucap/shared';
import { ModeBadge } from '@/components/common/ModeBadge.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useAppStore } from '@/stores/useAppStore.js';
import { useFavoriteStore } from '@/stores/useFavoriteStore.js';
import { createDemoFlowService } from '@/domain/demo/DemoFlowService.js';
import { createRecentAssetsService, type RecentAsset } from '@/domain/recent-assets/RecentAssetsService.js';
import { buildDeepLink } from '@/hooks/useDeepLinkContext.js';
import { SessionStatus, type MessageEvent, type SimulationRun, type Report, type TestSession } from '@k-ucap/shared';

export const DashboardPage = () => {
  const mode = useAppMode();
  const presentationMode = useAppStore((s) => s.presentationMode);
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const authStatus = useAuthStore((s) => s.authStatus);
  const userProfile = useAuthStore((s) => s.userProfile);
  const isReady = authStatus === 'READY';
  const messages = useMessageStore((s) => s.messages);
  const [events, setEvents] = useState<MessageEvent[]>([]);
  const [recentRuns, setRecentRuns] = useState<SimulationRun[]>([]);
  const [recentReports, setRecentReports] = useState<Report[]>([]);
  const [recentSessions, setRecentSessions] = useState<TestSession[]>([]);
  const [recentAssets, setRecentAssets] = useState<RecentAsset[]>([]);
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [demoProgress, setDemoProgress] = useState<number | null>(null);
  const [demoError, setDemoError] = useState<string | null>(null);
  const { messageEventRepository, simulationRunRepository, reportRepository, sessionRepository } = useRepositories();
  const allRepositories = useRepositories();
  const navigate = useNavigate();
  const favoriteItems = useFavoriteStore((s) => s.items);
  const favorites = useMemo(
    () => Object.values(favoriteItems).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [favoriteItems]
  );

  const demoSteps = ['시험 준비', '메시지 생성', '분석/저장', '보고서 생성'];

  useVirtualMessageBus();

  const loadRecentAssets = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const service = createRecentAssetsService(allRepositories);
      const assets = await service.loadRecentAssets(selectedProject.id);
      setRecentAssets(assets);
    } catch {
      setRecentAssets([]);
    }
  }, [allRepositories, selectedProject]);

  useEffect(() => {
    if (!selectedProject) {
      setEvents([]);
      setRecentRuns([]);
      setRecentReports([]);
      setRecentSessions([]);
      setRecentAssets([]);
      return;
    }
    const unsubscribe = messageEventRepository.subscribeToProject?.(selectedProject.id, (loaded) => {
      setEvents(loaded);
    });
    Promise.all([
      simulationRunRepository
        .findByProjectId(selectedProject.id)
        .then((loaded) => setRecentRuns(loaded.slice(0, 5)))
        .catch(() => setRecentRuns([])),
      reportRepository
        .findByProjectId(selectedProject.id)
        .then((loaded) => setRecentReports(loaded.slice(0, 5)))
        .catch(() => setRecentReports([])),
      sessionRepository
        .findByProjectId(selectedProject.id)
        .then((loaded) => setRecentSessions(loaded.slice(0, 5)))
        .catch(() => setRecentSessions([])),
      loadRecentAssets(),
    ]).catch(() => undefined);
    if (unsubscribe) {
      return () => unsubscribe();
    }
    messageEventRepository
      .findByProjectId(selectedProject.id)
      .then((loaded) => setEvents(loaded))
      .catch(() => setEvents([]));
    return;
  }, [
    messageEventRepository,
    simulationRunRepository,
    reportRepository,
    sessionRepository,
    selectedProject,
    loadRecentAssets,
  ]);

  const handleDemoStart = async () => {
    if (!selectedProject || !userProfile?.uid) {
      setDemoError('프로젝트와 사용자가 선택되어 있어야 데모를 실행할 수 있습니다.');
      return;
    }
    setDemoRunning(true);
    setDemoStep(0);
    setDemoProgress(0);
    setDemoError(null);
    try {
      setDemoStep(0);
      const service = createDemoFlowService(allRepositories);
      setDemoStep(1);
      setDemoProgress(30);
      const result = await service.run(selectedProject, userProfile.uid);
      setDemoProgress(80);
      setDemoStep(2);
      await loadRecentAssets();
      setDemoStep(3);
      setDemoProgress(100);
      navigate(
        buildDeepLink('/reports', {
          sessionId: result.sessionId,
          runId: result.runId,
        }) + `&report=${result.reportId}`
      );
    } catch (err) {
      setDemoError((err as Error).message);
      setDemoRunning(false);
    }
  };

  const allMessages = useMemo(() => {
    const eventMessages: MessageEvent[] = events;
    const storeMessages = messages.filter((m) => m.projectId === selectedProject?.id);
    const combined = [...storeMessages, ...eventMessages];
    const seen = new Set<string>();
    return combined.filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [events, messages, selectedProject]);

  const messageCount = getMessageDefinitions().length;
  const txCount = allMessages.filter((m) => m.direction === 'TX').length;
  const rxCount = allMessages.filter((m) => m.direction === 'RX').length;
  const passCount = allMessages.filter((m) => m.validation === ValidationSeverity.PASS).length;
  const warnCount = allMessages.filter((m) => m.validation === ValidationSeverity.WARN).length;
  const failCount = allMessages.filter((m) => m.validation === ValidationSeverity.FAIL).length;

  const projectName = selectedProject?.name ?? '프로젝트 미선택';
  const projectDescription = selectedProject?.description ?? '';

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        대시보드
      </Typography>
      {!presentationMode && <ModeBadge mode={mode} />}
      <Typography variant="h6" sx={{ mt: 2 }}>
        {projectName}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {projectDescription}
      </Typography>
      {!isReady && (
        <Alert severity="info" sx={{ mt: 2 }}>
          사용자 인증 정보를 불러오는 중입니다.
        </Alert>
      )}
      {isReady && !selectedProject && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          접근 가능한 프로젝트가 없습니다. Settings에서 프로젝트를 선택하거나 관리자에게 문의하세요.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">등록 메시지</Typography>
              <Typography variant="h4">{messageCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">송신 메시지</Typography>
              <Typography variant="h4">{txCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">수신 메시지</Typography>
              <Typography variant="h4">{rxCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">PASS/WARN/FAIL</Typography>
              <Typography variant="h6">
                {passCount}/{warnCount}/{failCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              데모 시작
            </Typography>
            <Typography variant="body2" color="text.secondary">
              한 번의 클릭으로 시험을 실행하고 보고서까지 생성합니다.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={handleDemoStart}
            disabled={demoRunning || !isReady || !selectedProject}
          >
            데모 실행
          </Button>
        </Box>
        {demoRunning && (
          <Box sx={{ mt: 2 }}>
            <Stepper activeStep={demoStep} alternativeLabel>
              {demoSteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {demoProgress !== null && <LinearProgress variant="determinate" value={demoProgress} sx={{ mt: 2 }} />}
          </Box>
        )}
        {demoError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {demoError}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          현재 시나리오
        </Typography>
        {selectedProject ? (
          <>
            <Typography>{selectedProject.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedProject.description}
            </Typography>
            <Chip label={selectedProject.status} size="small" sx={{ mt: 1 }} />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            선택된 프로젝트가 없습니다.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          최근 메시지
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allMessages.slice(0, 5).map((msg) => (
              <TableRow key={msg.id}>
                <TableCell>{new Date(msg.timestamp).toLocaleTimeString()}</TableCell>
                <TableCell>{msg.direction}</TableCell>
                <TableCell>{msg.messageType}</TableCell>
                <TableCell>{msg.name}</TableCell>
                <TableCell>
                  <Chip
                    label={msg.validation || 'N/A'}
                    size="small"
                    color={msg.validation === ValidationSeverity.PASS ? 'success' : 'warning'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              최근 실행 이력
            </Typography>
            {recentRuns.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                최근 실행 이력이 없습니다.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Generated</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentRuns.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell>{new Date(run.startedAt).toLocaleString()}</TableCell>
                      <TableCell>{run.name}</TableCell>
                      <TableCell>{run.generatedCount}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${run.passCount}/${run.warnCount}/${run.failCount}`}
                          size="small"
                          color={run.failCount > 0 ? 'error' : run.warnCount > 0 ? 'warning' : 'success'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              최근 보고서
            </Typography>
            {recentReports.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                최근 보고서가 없습니다.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>{new Date(report.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{report.title}</TableCell>
                      <TableCell>{report.summary.totalMessages}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${report.summary.passCount}/${report.summary.warnCount}/${report.summary.failCount}`}
                          size="small"
                          color={
                            report.summary.failCount > 0
                              ? 'error'
                              : report.summary.warnCount > 0
                                ? 'warning'
                                : 'success'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              최근 시험 세션
            </Typography>
            {recentSessions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                최근 시험 세션이 없습니다.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Started</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Messages</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentSessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>{new Date(session.startedAt).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={session.status}
                          size="small"
                          color={
                            session.status === SessionStatus.COMPLETED
                              ? 'success'
                              : session.status === SessionStatus.ERROR
                                ? 'error'
                                : 'info'
                          }
                        />
                      </TableCell>
                      <TableCell>{session.messageCount}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${session.passCount}/${session.warnCount}/${session.failCount}`}
                          size="small"
                          color={session.failCount > 0 ? 'error' : session.warnCount > 0 ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() =>
                            navigate(
                              buildDeepLink(session.status === SessionStatus.RUNNING ? '/scenario' : '/analyzer', {
                                sessionId: session.id,
                              })
                            )
                          }
                        >
                          {session.status === SessionStatus.RUNNING ? 'Workbench' : 'Open'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              최근 자산
            </Typography>
            {recentAssets.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                최근 사용한 자산이 없습니다.
              </Typography>
            ) : (
              <List dense>
                {recentAssets.map((asset) => (
                  <ListItem key={`${asset.type}-${asset.id}`} disablePadding>
                    <ListItemButton onClick={() => navigate(asset.href)}>
                      <ListItemText
                        primary={asset.name}
                        secondary={`${asset.type} • ${new Date(asset.updatedAt).toLocaleString()}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              즐겨찾기
            </Typography>
            {favorites.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                즐겨찾기에 추가한 자산이 없습니다. 자산 페이지에서 별표 아이콘을 눌러 추가하세요.
              </Typography>
            ) : (
              <List dense>
                {favorites.slice(0, 10).map((item) => (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton onClick={() => navigate(item.href)}>
                      <ListItemText
                        primary={item.name}
                        secondary={`${item.assetType} • ${new Date(item.createdAt).toLocaleString()}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
